// AbacatePay Configuration
// Centralized configuration for AbacatePay integration

interface AbacatePayConfig {
  apiKey: string;
  webhookSecret: string;
  environment: 'development' | 'production';
  enabled: boolean;
}

class AbacatePayConfiguration {
  private static instance: AbacatePayConfiguration;
  private config: AbacatePayConfig;

  private constructor() {
    const environment = import.meta.env.VITE_ABACATE_PAY_ENVIRONMENT || 'development';
    const isDev = environment === 'development';

    this.config = {
      apiKey: isDev 
        ? import.meta.env.VITE_ABACATE_PAY_API_KEY_DEV
        : import.meta.env.VITE_ABACATE_PAY_API_KEY_PROD,
      webhookSecret: import.meta.env.VITE_ABACATE_PAY_WEBHOOK_SECRET,
      environment: environment as 'development' | 'production',
      enabled: import.meta.env.VITE_ABACATE_PAY_ENABLED === 'true'
    };

    // Validate required configuration
    this.validateConfig();
  }

  public static getInstance(): AbacatePayConfiguration {
    if (!AbacatePayConfiguration.instance) {
      AbacatePayConfiguration.instance = new AbacatePayConfiguration();
    }
    return AbacatePayConfiguration.instance;
  }

  private validateConfig(): void {
    const errors: string[] = [];

    if (!this.config.apiKey) {
      const keyName = this.config.environment === 'development' 
        ? 'VITE_ABACATE_PAY_API_KEY_DEV'
        : 'VITE_ABACATE_PAY_API_KEY_PROD';
      errors.push(`Missing ${keyName} in environment variables`);
    }

    if (!this.config.webhookSecret) {
      errors.push('Missing VITE_ABACATE_PAY_WEBHOOK_SECRET in environment variables');
    }

    if (errors.length > 0 && this.config.enabled) {
      console.warn('AbacatePay Configuration Issues:', errors);
      console.warn('AbacatePay integration will be disabled');
      this.config.enabled = false;
    }
  }

  // Getters
  public getApiKey(): string {
    return this.config.apiKey;
  }

  public getWebhookSecret(): string {
    return this.config.webhookSecret;
  }

  public getEnvironment(): 'development' | 'production' {
    return this.config.environment;
  }

  public isEnabled(): boolean {
    return this.config.enabled;
  }

  public isDevelopment(): boolean {
    return this.config.environment === 'development';
  }

  public isProduction(): boolean {
    return this.config.environment === 'production';
  }

  // Debug info
  public getDebugInfo(): object {
    return {
      environment: this.config.environment,
      enabled: this.config.enabled,
      hasApiKey: !!this.config.apiKey,
      hasWebhookSecret: !!this.config.webhookSecret,
      apiKeyPrefix: this.config.apiKey ? this.config.apiKey.substring(0, 8) + '...' : 'Not set'
    };
  }
}

// Export singleton instance
export const abacatePayConfig = AbacatePayConfiguration.getInstance();

// Export types for use in other files
export type { AbacatePayConfig };